import { css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { alert } from "../../../elements/alert-dialog";
import { Dialog } from "../../../elements/dialog";
import "../../../elements/scroller";
import { app } from "../../../init";
import "./venue-form";
import type { VenueForm, VenueFormProps } from "./venue-form";

@customElement("ptc-venue-dialog")
export class VenueDialog extends Dialog<VenueFormProps, void> {
    readonly preventDismiss = true;

    @query("ptc-venue-form")
    private _form: VenueForm;

    @property()
    private _venueId: number | null = null;

    private get _isNew() {
        return this._venueId === null;
    }

    async show(venue?: VenueFormProps): Promise<void> {
        const promise = super.show();
        this._venueId = venue && typeof venue.id === "number" ? venue.id : null;
        if (venue) {
            await this.updateComplete;
            setTimeout(() => this._form.init(venue), 50);
        }
        return promise;
    }

    hide(): void {
        super.hide();
        this._form.form.reset();
    }

    private async _submit() {
        try {
            this.loading = true;
            this._form.setVenueNumberValidity();
            if (!this._form.form.reportValidity()) {
                return;
            }

            const data = this._form.data;
            if (!data) return;

            if (!this._isNew) {
                await app.updateVenue({ ...data, id: this._venueId! });
            } else {
                await app.createVenue(data);
            }

            this.done();
        } catch (e) {
            await alert(e.message, { type: "warning" });
        } finally {
            this.loading = false;
        }
    }

    static styles = [
        ...Dialog.styles,
        css`
            .inner {
                display: flex;
                flex-direction: column;
                max-width: 500px;
            }

            h1 {
                margin: 0;
                text-align: center;
            }

            .header,
            .footer {
                padding: 0.7em;
            }

            .footer {
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-gap: 0.7em;
            }

            ptc-scroller {
                flex: 1;
            }

            ptc-venue-form {
                display: block;
                margin: 0 0.7em;
            }
        `,
    ];

    renderContent() {
        return html`
            <div class="header">
                <h1>${this._venueId !== null ? "Standort Bearbeiten" : "Neuer Standort"}</h1>
            </div>

            <ptc-scroller>
                <ptc-venue-form
                    .usedVenueNumbers=${this._getUsedVenueNumbers()}
                    @submit=${this._submit}
                    @cancel=${() => this.done()}
                ></ptc-venue-form>
            </ptc-scroller>

            <div class="footer">
                <button class="primary stretch" @click=${this._submit}>Speichern</button>

                <button class="transparent stretch" @click=${this.done}>Abbrechen</button>
            </div>
        `;
    }

    private _getUsedVenueNumbers() {
        return app.venues
            .filter((v) => v.id !== this._venueId)
            .map((v) => v.venueNumber)
            .filter((n) => n != null) as number[];
    }
}
